<ng-container *ngIf="{
    sampleId: sampleId$ | async,
    caseId: caseId$ | async,
    hasUnsavedData: unsynced$ | async,
    loading: loading$ | async,
    totalCount: totalActiveDetections$ | async,
    totalObjectsCount: totalDetections$ | async,
    labelCounts: labelCounts$ | async,
    activeLabelCounts: activeLabelCounts$ | async,
    categoryCounts: sampleCategoryCounts$ | async,
    syncing: syncing$ | async,
    busy: busy$ | async,
    labels: labels$ | async,
    pipelines: pipelines$ | async,
    categories: categories$ | async,
    selectedAnalyst: selectedAnalyst$ | async,
    focusedRois: focusedRois$ | async,
    detailCollapsed: detailCollapsed$ | async,
    protocolCollapsed: protocolCollapsed$ | async,
    scale: scale$ | async,
    showOverlay: showOverlay$ | async,
    showRoiDetails: showRoiDetails$ | async,
    roiBoxVisible: roiBoxVisible$ | async,
    selectedAnalystIsCurrentUser: selectedAnalystIsCurrentUser$ | async,
    editingEnabled: editingEnabled$ | async,
    editingActive: editingActive$ | async,
    aiAnalysisViewOnly: aiAnalysisViewOnly$ | async,
    panelIds: panelIds$ | async,
    maxPanelsReached: maxPanelsReached$ | async,
    offlineMode: offlineMode$ | async,
    changes: changes$ | async,
    changeCountPerLabel: changeCountPerLabel$ | async,
    analysts: analysts$ | async,
    meOption: meOption$ | async,
    tileSource: tileSource$ | async,
    selectedAssetId: selectedAssetId$ | async,
    categoryChangeCounts: categoryChangeCounts$ | async,
    statsMode: statsMode$ | async,
    protocolLoading: protocolLoading$ | async,
    statsLoading: statsLoading$ | async,
    excludedFromCounter: excludedFromCounter$ | async,
    focusedPanelId: focusedPanelId$ | async,
    aiBannerVisible: aiBannerVisible$ | async,
    aiPollingExhausted: aiPollingExhausted$ | async,
    staleAnalysisBanner: staleAnalysisBanner$ | async,
    refreshRequired: refreshRequired$ | async,
    breadcrumbLevels: breadcrumbLevels$ | async
  } as state">
  <ts-analysis-layout class="full-height full-width" tsEditorKeybindings (esc)="clearFocusedRoi()" (ctrlS)="sync()"
    (tab)="focusNextPanel()" (shiftTab)="focusPreviousPanel()" (ctrlMetaPressed)="metaKeyPressed = $event"
    (metaNumber)="focusPanelByOrder($event)" (enter)="toggleDetail()">
    <ts-analysis-top-bar layoutTop variant="inline">
      <!-- LEFT: shared breadcrumb navigation -->
      <ts-navigation-breadcrumb barStart
        [levels]="applyAnalystDisabledState(state.breadcrumbLevels, state.selectedAnalyst)"
        menuClass="mosaic-breadcrumb-menu"
        (optionSelected)="onBreadcrumbSelect($event, state.caseId, state.meOption, state.analysts)"
        (levelOpened)="onBreadcrumbLevelOpened($event, state.sampleId, state.caseId)"
        (optionExpanded)="onBreadcrumbOptionExpanded($event)">
      </ts-navigation-breadcrumb>

      <!-- CENTER: mosaic layout toggles -->
      <div barCenter class="flex-row">
        <button mat-menu-item class="toolbar-action" [matTooltip]="'button.panel.left' | translate"
          [matTooltipShowDelay]="tootipDelay" (click)="toggleProtocol()">
          <i *ngIf="state.protocolCollapsed" class="ri-layout-left-line"></i>
          <i *ngIf="!state.protocolCollapsed" class="ri-layout-left-fill"></i>
        </button>
        <!-- REVIEW: consider moving into gallery search bar -->
        <button mat-menu-item class="toolbar-action" [matTooltip]="'button.panel.split' | translate"
          [matTooltipShowDelay]="tootipDelay" [disabled]="state.maxPanelsReached" (click)="addPanel()">
          <i class="ri-split-cells-horizontal"></i>
        </button>
        <button mat-menu-item class="toolbar-action" [matTooltip]="'button.panel.right' | translate"
          [matTooltipShowDelay]="tootipDelay" (click)="toggleDetail()">
          <i *ngIf="state.detailCollapsed" class="ri-layout-right-line"></i>
          <i *ngIf="!state.detailCollapsed" class="ri-layout-right-fill"></i>
        </button>
      </div>

      <!-- RIGHT: mosaic-specific actions -->
      <div barEnd class="toolbar-action flex-row">
        <button mat-menu-item class="toolbar-action" [matTooltip]="'tooltip.panel_microscope_view' | translate"
          (click)="navigateToMicro(state.caseId, state.sampleId)" [disabled]="state.busy"
          [matTooltipShowDelay]="tootipDelay">
          <i class="ri-microscope-line"></i>
        </button>

        <button mat-menu-item class="toolbar-action" [matTooltip]="'button.mode' | translate"
          [matMenuTriggerFor]="editorModeMenu" [disabled]="state.aiAnalysisViewOnly || state.refreshRequired"
          [matTooltipShowDelay]="tootipDelay">
          <i *ngIf="!state.editingEnabled || state.refreshRequired" class="ri-eye-line"></i>
          <i *ngIf="state.editingEnabled && !state.refreshRequired" class="ri-edit-line"></i>
        </button>

        <button *ngIf="!state.hasUnsavedData && !state.selectedAnalystIsCurrentUser && !state.aiAnalysisViewOnly"
          mat-menu-item class="toolbar-action" [disabled]="state.syncing" (click)="acceptCompleteAnalysis()"
          [matTooltip]="'info.accept_complete_analysis_tooltip' | translate" data-cy="mosaic-accept-analysis"
          [matTooltipShowDelay]="tootipDelay">
          <i class="ri-checkbox-circle-line"></i>
        </button>

        <button mat-menu-item class="toolbar-action" [matTooltip]="'analysis.revert_all_changes' | translate"
          [disabled]="!state.hasUnsavedData || !state.editingActive" (click)="openDiscardChangesDialog()"
          [matTooltipShowDelay]="tootipDelay">
          <i class="ri-arrow-go-back-line"></i>
        </button>
        <button [disabled]="!state.hasUnsavedData || state.offlineMode || !state.editingActive" mat-menu-item
          class="toolbar-action" [matTooltip]="'button.save' | translate"
          [class.bg-warning]="state.hasUnsavedData && !state.syncing" [class.text-white]="state.hasUnsavedData"
          (click)="sync()" [matTooltipShowDelay]="tootipDelay">
          <mat-spinner *ngIf="state.syncing; else saveIcon" class="relative-spinner" value="indeterminate"
            [diameter]="20"></mat-spinner>
          <ng-template #saveIcon>
            <i *ngIf="state.offlineMode" class="ri-cloud-off-line"></i>
            <i *ngIf="!state.offlineMode" class="ri-save-line"></i>
          </ng-template>
        </button>

        <button mat-menu-item class="toolbar-action" [matTooltip]="'button.menu.settings' | translate"
          [matMenuTriggerFor]="settingsMenu" [matTooltipShowDelay]="tootipDelay">
          <i class="ri-more-2-line"></i>
        </button>

        <button mat-menu-item class="toolbar-action" [matTooltip]="'button.close' | translate"
          [matTooltipShowDelay]="tootipDelay" [disabled]="state.busy" (click)="exit()">
          <i class="ri-close-fill"></i>
        </button>
      </div>
    </ts-analysis-top-bar>

    <div id="analysis-area" class="columns">
      <!-- PROTOCOL & STATS -->
      <div id="protocol" class="column widget hidden-scrollbar" [class.protocol-collapsed]="state.protocolCollapsed">
        <div class="flex-column sticky-top">
          <ng-container [ngTemplateOutlet]="protocolMenuBar"></ng-container>
        </div>

        <ts-protocol-panel id="protocol-panel" [sectionTemplate]="categoryStat" [labelTemplate]="labelStat"
          [options]="state.labels" [pipelines]="state.pipelines" [categories]="state.categories"
          [collapsed]="state.protocolCollapsed" (connectedDropListChanged)="panelDropLists = $event"
          [loading]="state.protocolLoading"></ts-protocol-panel>

        <div class="full-width sticky-bottom protocol-sticky-rows">
          <mat-divider></mat-divider>
          <ts-protocol-cell [label]="{ id: 'unlabeled', name: 'label.unlabeled', color: '#000000' }"
            [collapsed]="state.protocolCollapsed"
            [count]="(state.activeLabelCounts ?? state.labelCounts)?.get('unlabeled') ?? 0"
            [total]="state.totalCount ?? 0" [changeCount]="state.changeCountPerLabel.get('unlabeled') ?? 0"
            [hidePercentage]="true" (cropsLabelAdded)="removeAllLabelsOnFocusedItems()" [loading]="false"
            (click)="onSpecialSelected('unlabeled')"
            (contextMenuRequested)="onProtocolCellContextMenu($event, { kind: 'special', specialKind: 'unlabeled' })"></ts-protocol-cell>

          <ts-protocol-cell [label]="{ id: 'changed', name: 'core.changed', color: '#000000' }"
            [collapsed]="state.protocolCollapsed" [count]="state.changes.size ?? 0" [total]="state.totalCount ?? 0"
            [hidePercentage]="true" [loading]="false" (click)="onSpecialSelected('changed')"
            (contextMenuRequested)="onProtocolCellContextMenu($event, { kind: 'special', specialKind: 'changed' })"></ts-protocol-cell>

          <ts-protocol-cell [label]="{ id: 'selected', name: 'core.selected', color: '#000000' }"
            [collapsed]="state.protocolCollapsed" [count]="state.focusedRois?.size ?? 0" [total]="state.totalCount ?? 0"
            [hidePercentage]="true" [loading]="false" (click)="onSpecialSelected('selected')"
            (contextMenuRequested)="onProtocolCellContextMenu($event, { kind: 'special', specialKind: 'selected' })"></ts-protocol-cell>
        </div>
      </div>

      <!-- MOSAICS -->
      <div id="mosaics" class="column">
        <ts-ai-analysis-in-progress-banner [visible]="state.aiBannerVisible"
          [pollingExhausted]="state.aiPollingExhausted" [error]="state.aiAnalysisStatus === 'error'"
          [compact]="true"></ts-ai-analysis-in-progress-banner>
        <ts-ai-analysis-in-progress-banner [visible]="!!state.staleAnalysisBanner"
          [messageKey]="state.staleAnalysisBanner?.messageKey" [progress]="state.staleAnalysisBanner?.progress"
          [showRefresh]="!!state.staleAnalysisBanner?.showRefresh" [attention]="true"
          [compact]="true"></ts-ai-analysis-in-progress-banner>

        <div class="columns">
          <ts-mosaic-gallery *ngFor="let panelId of state.panelIds; trackBy: trackByPanelId"
            class="viewer column widget"
            [class.panel-focused]="state.panelIds?.length > 1 && panelId === state.focusedPanelId" [panelId]="panelId"
            [focused]="panelId === state.focusedPanelId" [sampleId]="state.sampleId"
            [removePanelDisabled]="state.panelIds?.length === 1" [syncing]="state.syncing" [loading]="state.loading"
            [scale]="state.scale" [roiBoxVisible]="state.roiBoxVisible" [connectedDropLists]="tabConnectedLists"
            [editingEnabled]="state.editingActive"
            (roiFocused)="setFocusedRoi($event)" (connectedDropListChanged)="updateTabDropLists($event, panelId)"
            (roiDoubleClicked)="openDetail()" (panelCloseRequested)="removePanel(panelId)"></ts-mosaic-gallery>
        </div>
      </div>

      <!-- POPUP CROP DETAIL -->
      <div id="detail-view" class="column widget" *ngIf="!state.detailCollapsed">
        <div class="detail-view-header flex-row justify-content-between widget">
          <div class="flex-row justify-content-start">
            <button mat-icon-button class="detail-view-header__btn" [disabled]="!state.selectedAssetId"
              [matTooltip]="'button.menu.filter_by_image' | translate"
              (click)="filterFocusedPanelByAsset(state.selectedAssetId)" [matTooltipShowDelay]="tootipDelay"
              #filterByAssetTooltip="matTooltip" (mouseleave)="filterByAssetTooltip.hide()">
              <mat-icon class="no-margin">image_search</mat-icon>
            </button>

            <button mat-icon-button class="detail-view-header__btn" [disabled]="!state.tileSource"
              [matTooltip]="'button.overlays' | translate" (click)="toggleOverlay()" [matTooltipShowDelay]="tootipDelay"
              #overlaysTooltip="matTooltip" (mouseleave)="overlaysTooltip.hide()">
              <mat-icon class="no-margin">{{ !state.showOverlay ? 'location_on' : 'location_off' }}</mat-icon>
            </button>

            <button mat-icon-button class="detail-view-header__btn" [disabled]="!state.focusedRois?.size"
              [matTooltip]="'button.filter_by_selected' | translate" (click)="filterFocusedPanelBySelected()"
              [matTooltipShowDelay]="tootipDelay" #filterBySelectedTooltip="matTooltip"
              (mouseleave)="filterBySelectedTooltip.hide()">
              <mat-icon class="no-margin">filter_list</mat-icon>
            </button>

            <button mat-icon-button class="detail-view-header__btn" [disabled]="!state.tileSource"
              [matTooltip]="'button.details' | translate" (click)="toggleRoiDetails()"
              [matTooltipShowDelay]="tootipDelay" #roiDetailsTooltip="matTooltip"
              (mouseleave)="roiDetailsTooltip.hide()">
              <mat-icon class="no-margin">{{ state.showRoiDetails ? 'info' : 'info_outline' }}</mat-icon>
            </button>
          </div>

          <button mat-icon-button class="detail-view-header__btn justify-content-end"
            [matTooltip]="'button.close_panel' | translate" (click)="toggleDetail()"
            [matTooltipShowDelay]="tootipDelay" #closeDetailTooltip="matTooltip"
            (mouseleave)="closeDetailTooltip.hide()">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <ts-crop-detail [editingEnabled]="state.editingActive" [overlay]="state.showOverlay"
          [detailsVisible]="state.showRoiDetails"></ts-crop-detail>
      </div>
    </div>
  </ts-analysis-layout>

  <!-- MENUS -->

  <mat-menu #settingsMenu="matMenu" class="widget">
    <ts-mosaic-tile-settings [tileSize]="state.scale" [roiBoxVisible]="state.roiBoxVisible"
      (tileSizeChanged)="setTileSize($event)"
      (roiBoxVisibleChanged)="setRoiBoxVisible($event)"></ts-mosaic-tile-settings>
  </mat-menu>

  <mat-menu #editorModeMenu="matMenu" class="widget">
    <ts-mosaic-editor-mode [editingEnabled]="state.editingEnabled"
      (editingEnabledChanged)="toggleEditing($event)"></ts-mosaic-editor-mode>
  </mat-menu>

  <mat-menu #statsModeMenu="matMenu" class="widget">
    <ts-stats-settings [statsMode]="state.statsMode"
      (statsModeChanged)="toggleCounterExclusionMode($event)"></ts-stats-settings>
  </mat-menu>

  <div #protocolCellContextMenuTrigger="matMenuTrigger" [matMenuTriggerFor]="protocolCellContextMenu"
    [style.position]="'fixed'" [style.left]="contextMenuPosition.x" [style.top]="contextMenuPosition.y"
    style="visibility: hidden;"></div>

  <mat-menu #protocolCellContextMenu="matMenu" class="widget">
    <ng-template matMenuContent let-payload="payload">
      <button mat-menu-item (click)="openInFocusedPanel(payload)">
        <span>{{ 'menu.protocol_cell.open_in_focused_panel' | translate }}</span>
        <span class="shortcut-hint">
          <i class="ri-cursor-line" aria-hidden="true"></i>
        </span>
      </button>
      <button mat-menu-item (click)="openInNewPanel(payload)">
        <span>{{ 'menu.protocol_cell.open_in_new_panel' | translate }}</span>
        <span class="shortcut-hint">
          <kbd>{{ isMacPlatform ? '⌘' : 'Ctrl' }}</kbd>
          <span>+</span>
          <i class="ri-cursor-line" aria-hidden="true"></i>
        </span>
      </button>
    </ng-template>
  </mat-menu>

  <!-- TEMPLATES -->
  <ng-template #categoryStat let-task>
    <ts-protocol-header class="full-width" [name]="task.name ?? task.id"
      [count]="state.categoryCounts.get(task.id) ?? 0" [total]="state.totalCount ?? 0"
      [changeCount]="state.categoryChangeCounts.get(task.id) ?? 0" [loading]="state.statsLoading"></ts-protocol-header>
  </ng-template>

  <ng-template #labelStat let-data>
    <ng-container [ngSwitch]="true">
      <ts-protocol-cell
        *ngSwitchCase="data.category.stepTask === 'roidetection' || data.category.stepTask === 'position'"
        class="full-width" [label]="data.label" [collapsed]="state.protocolCollapsed"
        [count]="(state.activeLabelCounts ?? state.labelCounts)?.get(data.label.id) ?? 0"
        [displayCount]="state.labelCounts?.get(data.label.id)" [total]="state.totalCount ?? 0"
        [changeCount]="state.changeCountPerLabel.get(data.label.id) ?? 0" [loading]="state.statsLoading"
        [excludedFromCounter]="state.excludedFromCounter.has(data.label.id)" [excludeable]="true"
        (selectLabel)="onLabelSelected(data.label.name)" (cropsLabelAdded)="addLabelToFocusedItems($event)"
        (excludeFromCounter)="toggleCounterExcludedLabel(data.label.id)"
        (contextMenuRequested)="onProtocolCellContextMenu($event, { kind: 'label', labelName: data.label.name })"></ts-protocol-cell>
      <div *ngSwitchDefault>{{ data.category.stepTask }}: {{ 'label.option_not_supported' | translate }}</div>
    </ng-container>
  </ng-template>

  <ng-template #protocolMenuBar>
    <div class="protocol-panel-controls-bar align-items-center justify-content-between flex-row">
      <button mat-icon-button class="protocol-panel-controls-bar__btn justify-content-start"
        [matTooltip]="'button.toggle_panel' | translate" [matTooltipShowDelay]="tootipDelay" (click)="toggleProtocol()">
        <i *ngIf="!state.protocolCollapsed" class="ri-layout-left-fill"></i>
        <i *ngIf="state.protocolCollapsed" class="ri-layout-left-line"></i>
      </button>

      <div *ngIf="!state.protocolCollapsed"
        class="total-count-text flex-row justify-content-center align-items-center flex-wrap gap-1 protocol-total-count">
        <span class="total-count-label">{{ 'label.total_count' | translate }}:</span>
        <ts-loading-attribute
          [value]="state.statsLoading ? undefined : state.totalObjectsCount ?? state.totalCount ?? 0"></ts-loading-attribute>
        <span *ngIf="!state.statsLoading && state.totalObjectsCount !== null">
          | {{ 'label.det_activas' | translate }} {{ state.totalCount }}</span>
      </div>

      <button mat-icon-button class="protocol-panel-controls-bar__btn justify-content-end"
        [matMenuTriggerFor]="statsModeMenu" [matTooltip]="'button.menu.settings' | translate"
        [matTooltipShowDelay]="tootipDelay" #statsModeTooltip="matTooltip" (mouseleave)="statsModeTooltip.hide()">
        <i *ngIf="state.statsMode === 'selective'" class="ri-discount-percent-line"></i>
        <i *ngIf="state.statsMode === 'complete'" class="ri-water-percent-line"></i>
      </button>
    </div>
    <mat-divider></mat-divider>
  </ng-template>
</ng-container>
